import React, { useState } from 'react';
import { useApi, api } from '../hooks/useApi';
import useMobile from '../hooks/useMobile';
import { useTheme } from '../context/ThemeContext';

const CATEGORIES = ['Grønnsaker og frukt','Meieri','Kjøtt og fisk','Bakst','Drikke','Frys','Hushold','Annet'];

export default function ShoppingPage() {
  const isMobile = useMobile();
  const { dark } = useTheme();
  const { data, refresh } = useApi('/api/shopping');
  const [newItem, setNewItem] = useState('');
  const [category, setCategory] = useState('Annet');
  const [qty, setQty] = useState('1');

  const lists = data?.lists || [];
  const items = data?.items || [];
  const listId = lists[0]?.id;

  const add = async e => {
    e.preventDefault();
    if (!newItem.trim() || !listId) return;
    await api.post('/api/shopping/items', { list_id: listId, name: newItem, quantity: qty, category });
    setNewItem(''); setQty('1');
    refresh();
  };

  const toggle = async item => {
    await api.put(`/api/shopping/items/${item.id}`, { checked: !item.checked });
    refresh();
  };

  const remove = async id => {
    await api.delete(`/api/shopping/items/${id}`);
    refresh();
  };

  const clearChecked = async () => {
    const checked = items.filter(i => i.checked);
    await Promise.all(checked.map(i => api.delete(`/api/shopping/items/${i.id}`)));
    refresh();
  };

  // Grupper etter kategori
  const grouped = CATEGORIES.reduce((acc, cat) => {
    const catItems = items.filter(i => i.category === cat);
    if (catItems.length) acc[cat] = catItems;
    return acc;
  }, {});
  const otherItems = items.filter(i => !CATEGORIES.includes(i.category));
  if (otherItems.length) grouped['Annet'] = [...(grouped['Annet']||[]), ...otherItems];

  const total   = items.length;
  const checked = items.filter(i => i.checked).length;

  const [showAddForm, setShowAddForm] = useState(false);
  const pageBg = dark ? '#0A0818' : '#EDE8FF';
  const txt    = dark ? '#E8E8F8' : '#1A1A35';
  const sub    = dark ? '#9090B8' : '#888';
  const border = dark ? 'rgba(255,255,255,.07)' : 'rgba(124,111,247,0.12)';

  if (isMobile) return (
    <div style={{ background:pageBg, minHeight:'100%', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>

      {/* Gradient Hero Header */}
      <div style={{
        background: dark
          ? 'linear-gradient(145deg, #2D1B69 0%, #1A1245 55%, #0D0B1E 100%)'
          : 'linear-gradient(145deg, #7C6FF7 0%, #9358E8 45%, #5E4AE3 100%)',
        padding: '52px 20px 24px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ position:'absolute', top:-40, right:-40, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:-60, left:-20, width:140, height:140, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }}/>
        <div style={{ position:'relative', zIndex:1, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', fontWeight:500, marginBottom:2 }}>Famora</div>
            <div style={{ fontSize:24, fontWeight:800, color:'#fff' }}>Handleliste</div>
            {total > 0 && (
              <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', marginTop:4 }}>
                {checked} av {total} handlet
              </div>
            )}
          </div>
          <button onClick={()=>setShowAddForm(v=>!v)}
            style={{ background:'rgba(255,255,255,0.15)', backdropFilter:'blur(10px)', color:'#fff', border:'1px solid rgba(255,255,255,0.25)', borderRadius:12, padding:'8px 16px', fontWeight:700, fontSize:14, cursor:'pointer' }}>
            {showAddForm ? 'Avbryt' : '+ Legg til'}
          </button>
        </div>
      </div>

      {/* Legg til form */}
      {showAddForm && (
        <form onSubmit={e=>{add(e);setShowAddForm(false);}} className="glass-card" style={{ margin:'12px 16px 8px', padding:14, display:'flex', flexDirection:'column', gap:10 }}>
          <input
            style={{ padding:'12px 14px', borderRadius:12, border:'1px solid '+(dark?'rgba(255,255,255,0.12)':'rgba(124,111,247,0.2)'), background: dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.7)', color:txt, fontSize:14, fontFamily:'inherit', outline:'none' }}
            placeholder="Varenavn *" value={newItem} onChange={e => setNewItem(e.target.value)} autoFocus required
          />
          <div style={{ display:'flex', gap:8 }}>
            <input
              style={{ flex:1, padding:'11px 12px', borderRadius:12, border:'1px solid '+(dark?'rgba(255,255,255,0.12)':'rgba(124,111,247,0.2)'), background: dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.7)', color:txt, fontSize:14, fontFamily:'inherit', outline:'none' }}
              placeholder="Antall" value={qty} onChange={e => setQty(e.target.value)}
            />
            <select value={category} onChange={e=>setCategory(e.target.value)}
              style={{ flex:2, padding:'11px 10px', borderRadius:12, border:'1px solid '+(dark?'rgba(255,255,255,0.12)':'rgba(124,111,247,0.2)'), background: dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.7)', color:txt, fontSize:13, fontFamily:'inherit', outline:'none' }}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <button type="submit" style={{ background:'linear-gradient(135deg,#7C6FF7,#9358E8)', color:'#fff', border:'none', borderRadius:12, padding:'12px', fontWeight:700, fontSize:15, cursor:'pointer' }}>Legg til</button>
        </form>
      )}

      {/* Fjern handlede */}
      {checked > 0 && (
        <div style={{ display:'flex', justifyContent:'flex-end', padding:'4px 16px 8px' }}>
          <button onClick={clearChecked} style={{ background:'none', border:'1px solid #7C6FF7', borderRadius:20, padding:'6px 14px', fontSize:12, fontWeight:700, color:'#7C6FF7', cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
            🗑 Tøm huket
          </button>
        </div>
      )}

      {/* Kategorisert liste */}
      <div style={{ padding:'4px 0 16px' }}>
        {Object.keys(grouped).length === 0 && !showAddForm && (
          <div style={{ textAlign:'center', padding:'60px 20px', color:sub }}>
            <div style={{ fontSize:48, marginBottom:12 }}>🛒</div>
            <div style={{ fontSize:16, fontWeight:700 }}>Tom handleliste</div>
            <div style={{ fontSize:14, marginTop:6, color:sub }}>Trykk for å legge til varer</div>
          </div>
        )}
        {Object.entries(grouped).map(([cat, catItems]) => (
          <div key={cat} style={{ margin:'0 16px 12px' }}>
            <div style={{ fontSize:11, fontWeight:700, color:sub, textTransform:'uppercase', letterSpacing:1, marginBottom:8, paddingLeft:4 }}>
              {cat}
            </div>
            <div className="glass-card" style={{ padding:0, overflow:'hidden' }}>
              {catItems.map((item, i) => (
                <div key={item.id} style={{ display:'flex', alignItems:'center', padding:'14px 16px', borderTop: i>0?'1px solid '+border:'none', gap:14 }}>
                  <div onClick={() => toggle(item)} style={{
                    width:26, height:26, borderRadius:'50%',
                    border: `2px solid ${item.checked ? '#7C6FF7' : border}`,
                    background: item.checked ? 'linear-gradient(135deg,#7C6FF7,#9358E8)' : 'transparent',
                    cursor:'pointer', flexShrink:0,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    color:'#fff', fontSize:14, fontWeight:800,
                  }}>
                    {item.checked && '✓'}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:15, fontWeight:600, color:txt, textDecoration:item.checked?'line-through':'none', opacity:item.checked?0.6:1 }}>{item.name}</div>
                    {item.quantity && item.quantity !== '1' && (
                      <div style={{ fontSize:12, color:sub, marginTop:1 }}>{item.quantity}</div>
                    )}
                  </div>
                  <button onClick={() => remove(item.id)} style={{ background:'none', border:'none', color:sub, cursor:'pointer', fontSize:18, padding:'0 2px', flexShrink:0 }}>✕</button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <form onSubmit={add} style={s.form}>
        <input style={s.inp} placeholder="Legg til vare..." value={newItem} onChange={e => setNewItem(e.target.value)} autoFocus />
        <input style={{...s.inp, width:60}} placeholder="Ant." value={qty} onChange={e => setQty(e.target.value)} />
        <select style={s.sel} value={category} onChange={e => setCategory(e.target.value)}>
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
        <button style={s.addBtn} type="submit">+</button>
      </form>

      {total > 0 && (
        <div style={s.progress}>
          <div style={s.progressBar}>
            <div style={{...s.progressFill, width: `${(checked/total)*100}%`}} />
          </div>
          <span style={s.progressTxt}>{checked} av {total} hentet</span>
          {checked > 0 && <button style={s.clearBtn} onClick={clearChecked}>Fjern hentede</button>}
        </div>
      )}

      {Object.keys(grouped).length === 0 && <div style={s.empty}>Handlelisten er tom 🛒<br/><small>Legg til varer ovenfor</small></div>}

      {Object.entries(grouped).map(([cat, catItems]) => (
        <div key={cat} style={s.section}>
          <div style={s.catTitle}>{catEmoji(cat)} {cat}</div>
          {catItems.map(item => (
            <div key={item.id} style={{...s.item, opacity: item.checked ? 0.45 : 1}}>
              <button style={{...s.check, ...(item.checked ? s.checkDone : {})}} onClick={() => toggle(item)}>
                {item.checked && '✓'}
              </button>
              <span style={{...s.name, textDecoration: item.checked ? 'line-through' : 'none'}}>{item.name}</span>
              <span style={s.qty}>{item.quantity}</span>
              <button style={s.del} onClick={() => remove(item.id)}>✕</button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function catEmoji(cat) {
  const map = {'Grønnsaker og frukt':'🥦','Meieri':'🥛','Kjøtt og fisk':'🥩','Bakst':'🍞','Drikke':'🥤','Frys':'🧊','Hushold':'🧴','Annet':'📦'};
  return map[cat] || '📦';
}

const s = {
  form:        { display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' },
  inp:         { flex:1, minWidth:120, padding:'10px 14px', borderRadius:10, border:'1px solid var(--input-bdr)', fontFamily:'inherit', fontSize:14, color:'var(--input-txt)', outline:'none', background:'var(--input-bg)' },
  sel:         { padding:'10px 12px', borderRadius:10, border:'1px solid var(--input-bdr)', fontFamily:'inherit', fontSize:14, background:'var(--input-bg)', color:'var(--input-txt)', outline:'none' },
  addBtn:      { padding:'10px 18px', borderRadius:10, border:'none', background:'#7C6FF7', color:'#fff', fontSize:20, fontWeight:700, cursor:'pointer' },
  progress:    { display:'flex', alignItems:'center', gap:10, marginBottom:16, background:'var(--card)', padding:'10px 14px', borderRadius:12, border:'1px solid var(--bdr)' },
  progressBar: { flex:1, height:8, background:'var(--bdr)', borderRadius:4, overflow:'hidden' },
  progressFill:{ height:'100%', background:'#34D399', borderRadius:4, transition:'width .3s' },
  progressTxt: { fontSize:13, fontWeight:600, color:'var(--txm)', whiteSpace:'nowrap' },
  clearBtn:    { fontSize:12, fontWeight:700, color:'#F7605C', background:'none', border:'none', cursor:'pointer', whiteSpace:'nowrap' },
  empty:       { textAlign:'center', padding:60, color:'var(--txl)', fontSize:15, lineHeight:2 },
  section:     { marginBottom:16 },
  catTitle:    { fontSize:12, fontWeight:700, color:'var(--txl)', textTransform:'uppercase', letterSpacing:.5, marginBottom:6, padding:'0 4px' },
  item:        { display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'var(--card)', borderRadius:10, border:'1px solid var(--bdr)', marginBottom:5 },
  check:       { width:22, height:22, borderRadius:6, border:'2px solid var(--bdr)', background:'var(--card)', cursor:'pointer', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'#fff' },
  checkDone:   { background:'#34D399', borderColor:'#34D399' },
  name:        { flex:1, fontSize:14, fontWeight:500, color:'var(--txt)' },
  qty:         { fontSize:13, color:'var(--txl)', minWidth:28, textAlign:'right' },
  del:         { background:'none', border:'none', color:'var(--txl)', cursor:'pointer', fontSize:15, padding:'0 2px' },
};
